// connectProducer.ts
import type { SqlError } from "@effect/sql/SqlError";
import { captureException } from "@sentry/react";
import { Effect, pipe, Option, Schema as S, Exit, identity, ParseResult, Cause, Either } from "effect";
import { isNonEmptyReadonlyArray } from "effect/Array";
import type { NoSuchElementException, YieldableError } from "effect/Cause";
import type { ConfigError } from "effect/ConfigError";
import type { ParseError } from "effect/ParseResult";
import { util } from "~/utils/util";
 
// Function to connect the producer
export const skipInEnvironment = (skipFunction: () => Promise<void>, environment: string) =>
  import.meta.env.VITE_NODE_ENV === environment ?
    Effect.succeed(`Skipping ${skipFunction.name} in ${environment} environment`)
    :
    Effect.tryPromise({
      try: () => skipFunction(),
      // remap the error
      catch: (unknown) => new Error(`${skipFunction} execution error ${unknown}`)
    });
//TODO: try to use dependency injection


export type Result<T> = {
  hasError: boolean;
  errorMessage?: string;
  error?: unknown;
  data?: T; // Adjust based on your data schema
};

function createResponse<T>(data: T): Result<T> {
  return {
    hasError: false,
    data,
  };
}

function createErrorResponse<T>(
  error: unknown,
  errorMessage: string
): Result<T> {
  return {
    hasError: true,
    errorMessage,
    error
  };
}
export const withErrorHandling = <R extends readonly unknown[], E, A>(
  effect: Effect.Effect<R, E, A>
) =>
  pipe(
    effect,
    Effect.andThen((data) =>
      isNonEmptyReadonlyArray(data) ? Option.some(data) : Option.none()
    ),
    Effect.map((data) => createResponse(data)),
    Effect.catchAll((error) => {
      // glitch coe goes here
      // ticket based on error
      return Effect.succeed(createErrorResponse(error, "Failed to load trip data."))
    }
    )
  );



type HandleLoaderResult = <A, I>(program: Effect.Effect<A>, valueSchema: S.Schema<A, I>) => Promise<I>
export const handleLoaderResult: HandleLoaderResult = async (program, valueSchema) => {
  return pipe(
    program,
    Effect.andThen(S.encode(valueSchema)),
    Effect.runPromise
  )
}
export const ListEmpty = S.TaggedStruct("EMTPY", {})

export const makeErrorEmpty = () => Effect.succeed(ListEmpty.make({}))

export const ListSuccess = <A, E = A>(itemSchema: S.Schema<A, E>) =>
  S.TaggedStruct("SUCCESS", {
    itemList: S.Array(itemSchema),
  });

export const ItemSuccess = <A, E = A>(itemSchema: S.Schema<A, E>) =>
  S.TaggedStruct("SUCCESS", {
    item: itemSchema
  })

export const IdName = S.Struct({
  id: S.Union(S.Number, S.NumberFromString),
  name: S.String
});

export const IdNameOptional = S.Struct({
  id: S.optional(S.Union(S.Number, S.NumberFromString)),


  name: S.optional(S.String)
});
export const truckType = S.Struct({
  id: S.Union(S.Number, S.NumberFromString),
  name: S.String,
  truck_group_id: S.Union(S.Number, S.NumberFromString)
})

export type truckType = typeof truckType.Type

export const makeIDNameEmpty = IdName.make({ id: 0, name: "" })
export const IdNames = S.Array(IdName)
export const IdNamesSuccess = ListSuccess(IdName)
export const IdNamesResult = S.Union(IdNamesSuccess, ListEmpty)

export type IdName = typeof IdName.Type
export type IdNames = typeof IdNames.Type
export type IdNamesSuccess = typeof IdNamesSuccess.Type
export type IdNamesResult = typeof IdNamesResult.Type

export const ActionErrorApplication = S.TaggedStruct("ACTION_ERROR_APPLICATION", {
  message: S.String,
  cause: S.optional(S.String)
});


const ActionErrorEntry = S.Struct({
  path: S.Array(S.Union(S.String, S.Number, S.Symbol)),
  message: S.String
})

export const ActionSuccess = S.TaggedStruct("ACTION_SUCCESS", { redirectTo: S.String, message: S.optional(S.String), data: S.optional(S.Any) })
export const ActionReturnResult = S.TaggedStruct("ACTION_RESULT", { message: S.optional(S.String), data: S.optional(S.Any) })

export const ActionErrorValidation = S.TaggedStruct("ACTION_ERROR_VALIDATION", {
  errors: S.Array(ActionErrorEntry),
  event_at: S.String
})
export const ActionRedirectErrorServer = S.TaggedStruct("ACTION_REDIRECT_ERROR_SERVER", { message: S.String, event_at: S.String, redirect_to: S.String })
export const ActionErrorServer = S.TaggedStruct("ACTION_ERROR_SERVER", { message: S.String, event_at: S.String, data: S.optional(S.Any) })
export const ActionErrorSQL = S.TaggedStruct("ACTION_ERROR_SQL", {
  message: S.String
})
export const ActionResult = S.Union(ActionSuccess, ActionErrorValidation, ActionErrorServer, ActionErrorSQL, ActionReturnResult)

export type ActionResult = typeof ActionResult.Type
export type ActionReturnResult = typeof ActionReturnResult.Type
export type ActionErrorValidation = typeof ActionErrorValidation.Type
export type ActionErrorServer = typeof ActionErrorServer.Type
export type ActionErrorSQL = typeof ActionErrorSQL.Type
export type ActionResultEncoded = typeof ActionResult.Encoded
export type ActionRedirectErrorServer = typeof ActionRedirectErrorServer.Type



const handleActionValidationError = (e: ParseError) => {
  console.error(e)
  return Effect.succeed(ActionErrorValidation.make({ errors: ParseResult.ArrayFormatter.formatErrorSync(e), event_at: util.currentTime() }))
}


export const handleSQLError = (e: SqlError) => {
  return Effect.fail(
    ActionErrorSQL.make({
      message: typeof e.cause === "object" && e.cause !== null && "message" in e.cause
        ? (e.cause as { message: string }).message
        : typeof e.message === "string"
          ? e.message
          : "Unknown SQL Error"
    })
  );
};

  interface HandleActionResultOpts {
  request: Request,
  event: string,
}

type HandleActionResult = (program: Effect.Effect<ActionResult, ParseError | SqlError | YieldableError | ActionErrorServer | Error>, opts: HandleActionResultOpts) => Promise<ActionResultEncoded>

export const handleActionResult: HandleActionResult = async (program) => {
  console.log("in actionResult");

  const resultExit = await pipe(
    program,
    Effect.catchTag("ParseError", handleActionValidationError),
    Effect.catchTag("SqlError", handleSQLError),//unknown error
    Effect.andThen(r => S.encode(ActionResult)(r)),
    // Effect.tap((data) => Effect.log(`INFO: ${data._tag}`)),
    Effect.runPromiseExit
  )
  return Exit.match(resultExit, {
    onSuccess: identity,
    onFailure: (err) => {
      console.error("Exit.match", err)
      //TODO: J need to fix this
      // captureRemixServerException(err, "EventPlaybookeCreate", opts.request)
      const failure = Cause.failureOrCause(err);
      const errorMessage = Either.isLeft(failure) ? failure.left?.message ?? "Unknown error occurred" : "Unknown error occurred";
      const data = Either.isLeft(failure) 
      ? 'data' in failure.left 
        ? failure.left.data 
        : {} 
      : {};
      captureException(JSON.stringify(err));
      return ActionErrorServer.make({ message: errorMessage, event_at: util.currentTime(), data });

    }
  })
}

export const handleTaggedErrorWithStackInfo = (stack?: string) => (error: SqlError) => {
  const location = stack?.split('\n').find((line) => line.includes('loader'))?.trim();
  const errorMessage = `${error._tag} ${String(error.cause) }${location ? ` ${location}` : ""}`;
  return Effect.die(errorMessage);
};

// todo : commonize the errorMessage for ParseError and configError
export const withCommonErrorHandling = <R, A>(
  effect: Effect.Effect<R, NoSuchElementException | ParseError | SqlError | ConfigError, A>,
  stack?: string
) => 
  pipe(
    effect,
    Effect.catchTag("NoSuchElementException", makeErrorEmpty),  
    Effect.catchTag("ParseError", Effect.orDie), 
    Effect.catchTag("SqlError", handleTaggedErrorWithStackInfo(stack)),
    Effect.catchTag("ConfigError", Effect.die)
  );
